import { type FormInstance, Checkbox, Form } from 'antd5';
import { type CheckboxChangeEvent } from 'antd5/lib/checkbox';
import { useEffect, useState } from 'react';

import { DeliveryOrganizationChooser } from 'components/choosers/DeliveryOrganizationChooser';
import * as taskData from 'components/data/tasks/tasks';
import {
  type AtlasGqlInspectionTask,
  AtlasGqlFilterOperator,
  AtlasGqlWorkContainerTypeOption,
  useGetInspectionTasksQuery,
} from 'types/atlas-graphql';
import { useAccountContext } from 'utils/account/AccountContext';
import { STANDARD_COLUMN_SCROLL_Y } from 'utils/constants';
import { useFeatures } from 'utils/features';
import { WORK_CONTAINER_FILTER_IMPROVEMENT } from 'utils/release-toggles';
import { useSelectableRecords } from 'utils/selectable';

import { StyledTable } from './InspectionTaskChooser.style';

interface InspectionTaskChooserProps {
  assetId?: string;
  onChange: (value?: string | null, forOrganization?: string | null) => void;
  value?: string | null;
  form: FormInstance;
}
export const InspectionTaskChooser = ({
  assetId,
  onChange,
  form,
  ...props
}: InspectionTaskChooserProps) => {
  const { hasReleaseToggle } = useAccountContext();
  const hasFilterImprovementRT = hasReleaseToggle(WORK_CONTAINER_FILTER_IMPROVEMENT);
  const { WORK_ORDERS, INSPECTION_DELIVERY } = useFeatures().features;

  const [selectedTaskId, setSelectedTaskId] = useState<string | null | undefined>();

  const { value: formTaskId } = props;

  // get eligible tasks (inspection tasks that are non archived)
  const { data: inspectionTasksData, loading: inspectionTasksLoading } = useGetInspectionTasksQuery(
    {
      variables: {
        assetIds: [assetId as string],
        offset: 0,
        limit: 0,
        filterBy: [
          {
            key: 'type',
            values: [AtlasGqlWorkContainerTypeOption.InspectionTask],
            operator: AtlasGqlFilterOperator.Equals,
          },
          {
            key: 'archived',
            values: [],
            operator: AtlasGqlFilterOperator.IsNull,
          },
        ],
        sortBy: [],
      },
      fetchPolicy: 'network-only',
      skip: !assetId,
    }
  );

  // filter out inspections tasks that have inspections
  const filteredTasks = (inspectionTasksData?.paginatedTasks?.items ?? []).filter(task => {
    return !(task as AtlasGqlInspectionTask).inspection;
  });

  // handle selected to create a new task
  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    onClearSelection();

    const value = e.target.checked ? null : undefined;
    setSelectedTaskId(value);
    onChange(value, null);
  };

  // handle selections in the table;
  const handleSelectionChange = (selected: AtlasGqlInspectionTask[]) => {
    const value = selected[0]?.id;
    setSelectedTaskId(value);
    onChange(value, null);
  };

  const { rowSelection, onClearSelection } =
    useSelectableRecords<AtlasGqlInspectionTask>(handleSelectionChange);

  // handles if the form resets the task id
  useEffect(() => {
    if (formTaskId === undefined && formTaskId !== selectedTaskId) {
      onClearSelection();
      setSelectedTaskId(formTaskId);
    }
  }, [formTaskId, selectedTaskId, onClearSelection]);

  const isCreatingNewTask = selectedTaskId === null;

  return (
    <>
      <Form.Item>
        <Checkbox
          data-testid="create-new-task-checkbox"
          checked={isCreatingNewTask}
          onChange={handleCheckboxChange}
        >
          Create new Task
        </Checkbox>
      </Form.Item>
      {isCreatingNewTask ? (
        <>
          {INSPECTION_DELIVERY && assetId ? (
            <Form.Item name="forOrganization" label="For Organization">
              <DeliveryOrganizationChooser
                formKey="forOrganization"
                form={form}
                assetIds={[assetId]}
              />
            </Form.Item>
          ) : null}
        </>
      ) : (
        <>
          {assetId ? (
            // @ts-ignore JZ: it doesn't like some of the column definitions.
            <StyledTable
              data-testid="tasks-selection-table"
              loading={inspectionTasksLoading}
              columns={[
                taskData.taskNumber,
                ...(WORK_ORDERS ? [taskData.atlasWorkOrder(15)] : []),
                taskData.clientAtlasType,
                taskData.status,
                taskData.dueDate(hasFilterImprovementRT),
                taskData.created,
              ]}
              dataSource={filteredTasks}
              rowSelection={{
                ...rowSelection,
                type: 'radio',
                hideSelectAll: true,
              }}
              rowKey="id"
              size="small"
              pagination={false}
              scroll={STANDARD_COLUMN_SCROLL_Y}
            />
          ) : null}
        </>
      )}
    </>
  );
};
